import dayjs from 'dayjs';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  getFirestore,
  onSnapshot,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

// Загальні стилі для форми модального вікна
const modalFormStyles = {
  inputGroup: {
    marginBottom: '16px',
    width: '100%',
  },
  label: {
    display: 'block',
    color: '#E0E0E0',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: '500',
  },
  input: {
    width: '95%',
    padding: '10px 12px',
    backgroundColor: '#323232',
    color: '#fff',
    border: '1px solid #555',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
  },
  textarea: {
    width: '95%',
    padding: '10px 12px',
    backgroundColor: '#323232',
    color: '#fff',
    border: '1px solid #555',
    borderRadius: '8px',
    fontSize: '14px',
    minHeight: '100px',
    resize: 'vertical',
    outline: 'none',
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: '#323232',
    color: '#fff',
    border: '1px solid #555',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
  },
  errorText: {
    color: '#FF6B6B',
    fontSize: '12px',
    marginTop: '4px',
  },
  buttonGroup: {
    display: 'flex',
    gap: '12px',
    marginTop: '24px',
    justifyContent: 'flex-end',
  },
  button: {
    padding: '10px 16px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: '500',
    fontSize: '14px',
    transition: 'all 0.2s ease',
  },
  primaryButton: {
    backgroundColor: '#FF8E04',
    color: '#fff',
  },
  secondaryButton: {
    backgroundColor: '#444',
    color: '#fff',
  },
};

const Promo = () => {
  const db = getFirestore();
  const [promocodes, setPromocodes] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPromo, setSelectedPromo] = useState(null);
  const [recipientType, setRecipientType] = useState('all');
  // Реєстрація форми для створення промокоду
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '',
      percent: '',
      type: '',
      expirationDate: dayjs().add(1, 'day').format('YYYY-MM-DD'),
    },
  });
  // Реєстрація форми для модального вікна
  const {
    control: modalControl,
    handleSubmit: handleModalSubmit,
    reset: resetModal,
    formState: { errors: modalErrors },
  } = useForm({
    defaultValues: {
      modalTitle: '',
      modalBody: '',
      modalScheduleTime: dayjs().add(1, 'hour').format('YYYY-MM-DDTHH:mm'),
      modalDataTitle: '',
      modalDataText: '',
      phoneNumbers: '',
    },
  });

  // Функція для відкриття модального вікна
  const openModal = (promo) => {
    setSelectedPromo(promo);
    setIsModalOpen(true);
  };

  // Функція для закриття модального вікна
  const closeModal = () => {
    setSelectedPromo(null);
    setIsModalOpen(false);
    resetModal();
  };

  // Ефект для отримання промокодів з Firestore
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'promocodes'),
      (snapshot) => {
        const promocodesData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setPromocodes(promocodesData);
      },
      (error) => {
        console.error('Error listening to promocodes:', error);
      }
    );
    return () => unsubscribe();
  }, [db]);

  // Автоматичне видалення застарілих промокодів
  const removeExpiredPromocodes = useCallback(async () => {
    const now = new Date();
    const expiredPromocodes = promocodes.filter(
      (promo) => promo.expirationDate.toDate() < now
    );
    for (const promo of expiredPromocodes) {
      await deleteDoc(doc(db, 'promocodes', promo.id));
    }
  }, [db, promocodes]);

  useEffect(() => {
    removeExpiredPromocodes();
  }, [removeExpiredPromocodes]);

  const onSubmit = async (data) => {
    try {
      const { name, percent, type, expirationDate } = data;
      if (!name || !percent || !type || !expirationDate) {
        return alert("Всі поля є обов'язковими");
      }

      const expirationDateObj = dayjs(expirationDate).toDate();
      const promocodesRef = collection(db, 'promocodes');

      const q = query(promocodesRef, where('name', '==', name));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        return alert("Промокод з таким ім'ям вже існує");
      }

      await addDoc(promocodesRef, {
        name,
        percent: parseInt(percent, 10),
        type,
        expirationDate: expirationDateObj,
        mailed: false,
      });
      reset();
      alert('Промокод успішно створено');
    } catch (error) {
      console.error('Помилка при створенні промокоду:', error);
      alert('Не вдалося створити промокод');
    }
  };

  const onSubmitModal = async (data) => {
    try {
      const {
        modalTitle,
        modalBody,
        modalScheduleTime,
        modalDataTitle,
        modalDataText,
        phoneNumbers,
      } = data;

      if (!selectedPromo) {
        throw new Error('Промокод не вибрано');
      }

      const localTime = new Date(modalScheduleTime);
      const utcTime = new Date(
        localTime.getTime() - localTime.getTimezoneOffset() * 60000
      );

      const now = new Date();
      if (utcTime <= now) {
        throw new Error('Дата відправки повинна бути у майбутньому');
      }

      const send = httpsCallable(getFunctions(), 'sendPushNotification');
      let tokens = [];
      let emails = [];
      if (recipientType === 'selective') {
        const phoneList = phoneNumbers.split(',').map((phone) => phone.trim());
        if (phoneList.length === 0) {
          throw new Error('Номери телефонів не вказані');
        }

        const usersRef = collection(
          db,
          `users/${selectedPromo.type === 'sub' ? 'master' : 'client'}/profiles`
        );
        const q = query(usersRef, where('phone', 'in', phoneList));
        const querySnapshot = await getDocs(q);

        querySnapshot.docs.forEach((doc) => {
          tokens.push(doc.data().fcmToken);
          emails.push(doc.data().email);
        });
        if (tokens.length === 0) {
          throw new Error('Не знайдено користувачів з цими номерами телефонів');
        }
      }

      send({
        title: modalTitle,
        body: modalBody,
        emails: recipientType === 'selective' ? emails : undefined,
        tokens: recipientType === 'selective' ? tokens : undefined,
        topic:
          recipientType === 'all'
            ? selectedPromo.type === 'sub'
              ? 'master'
              : 'client'
            : undefined,
        data: {
          title: modalDataTitle,
          text: modalDataText,
        },
        time: utcTime.toISOString(),
      });

      await updateDoc(doc(db, 'promocodes', selectedPromo.id), {
        mailed: true,
        merge: true,
      });
      setRecipientType('all');
      closeModal();
      alert('Повідомлення заплановано успішно');
    } catch (error) {
      console.error('Помилка при плануванні повідомлення:', error);
      alert(error.message);
    }
  };

  // Обробник видалення промокоду
  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, 'promocodes', id));
      alert('Промокод видалено успішно');
    } catch (error) {
      console.error('Помилка при видаленні промокоду:', error);
      alert('Не вдалося видалити промокод');
    }
  };

  return (
    <div
      style={{
        padding: 20,
        backgroundColor: '#121212',
        color: 'white',
        display: 'flex',
        gap: 24,
        flexWrap: 'wrap',
        overflowY: 'auto',
      }}
    >
      {/* Форма для створення промокоду */}
      <div
        style={{
          width: 450,
          backgroundColor: '#323232',
          height: 'fit-content',
          justifyContent: 'center',
          display: 'flex',
          gap: 16,
          rowGap: 16,
          padding: 24,
          flexWrap: 'wrap',
          alignItems: 'center',
          borderRadius: 16,
          border: '1px solid #555',
        }}
      >
        {/* Поле для імені промокоду */}
        <Controller
          control={control}
          name='name'
          rules={{ required: `Промокод обов'язковий`, minLength: 4 }}
          render={({ field: { onChange, value } }) => (
            <div>
              <input
                style={{
                  borderRadius: 24,
                  paddingLeft: 10,
                  width: 180,
                  height: 26,
                  border: '1px solid #ccc',
                }}
                maxLength={15}
                placeholder='Промокод'
                value={value}
                onChange={(e) => onChange(e.target.value)}
              />
              {errors.name && (
                <div style={{ color: 'red', padding: 10 }}>
                  {errors.name.message}
                </div>
              )}
            </div>
          )}
        />
        {/* Поле для відсотка знижки */}
        <Controller
          control={control}
          name='percent'
          rules={{
            required: `Це поле обов'язково`,
            min: { value: 1, message: 'Min 1%' },
            max: { value: 100, message: 'Max 100%' },
          }}
          render={({ field: { onChange, value } }) => (
            <div>
              <input
                style={{
                  borderRadius: 24,
                  paddingLeft: 10,
                  width: 180,
                  height: 26,
                  border: '1px solid #ccc',
                }}
                placeholder='Відсоток знижки (1-100)'
                value={value}
                onChange={(e) => onChange(e.target.value)}
                type='number'
              />
              {errors.percent && (
                <div style={{ color: 'red', padding: 10 }}>
                  {errors.percent.message}
                </div>
              )}
            </div>
          )}
        />
        {/* Поле для вибору типу промокоду */}
        <Controller
          control={control}
          name='type'
          rules={{ required: `Тип обов'язковий` }}
          render={({ field: { onChange, value } }) => (
            <div>
              <select
                style={{
                  borderRadius: 24,
                  paddingLeft: 10,
                  width: 200,
                  height: 44,
                  border: '1px solid #ccc',
                }}
                value={value}
                onChange={(e) => onChange(e.target.value)}
              >
                <option value='' disabled>
                  Оберіть тип
                </option>
                <option value='sub'>Підписка</option>
                <option value='prod'>Продукт</option>
              </select>
              {errors.type && (
                <div style={{ color: 'red', padding: 10 }}>
                  {errors.type.message}
                </div>
              )}
            </div>
          )}
        />
        {/* Поле для терміну дії */}
        <Controller
          control={control}
          name='expirationDate'
          rules={{
            required: `Термін дії обов'язковий`,
            validate: {
              notBeforeToday: (value) => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                return (
                  new Date(value) >= today ||
                  'Дата не може бути раніше сьогоднішньої'
                );
              },
              notAfterOneYear: (value) => {
                const today = new Date();
                const maxDate = new Date(today);
                maxDate.setFullYear(today.getFullYear() + 1);
                return (
                  new Date(value) <= maxDate || 'Максимальний термін - 1 рік'
                );
              },
            },
          }}
          render={({ field: { onChange, value } }) => (
            <div>
              <input
                aria-label='Date'
                type='date'
                value={value ?? new Date().toISOString().split('T')[0]}
                onChange={onChange}
                min={dayjs().add(1, 'day').format('YYYY-MM-DD')}
                max={dayjs().add(1, 'year').format('YYYY-MM-DD')}
                style={{
                  borderRadius: 24,
                  paddingLeft: 10,
                  height: 26,
                  border: '1px solid #ccc',
                  width: 180,
                  backgroundColor: '#fff',
                  color: '#333',
                  fontSize: 14,
                }}
              />
              {errors.expirationDate && (
                <div style={{ color: 'red', marginTop: '5px', fontSize: 12 }}>
                  {errors.expirationDate.message}
                </div>
              )}
            </div>
          )}
        />
        {/* Кнопка для створення промокоду */}
        <button
          style={{
            backgroundColor: '#ff8e04',
            color: 'white',
            border: 'none',
            height: 36,
            width: 190,
            borderRadius: 24,
            cursor: 'pointer',
          }}
          onClick={handleSubmit(onSubmit)}
        >
          Створити промокод
        </button>
      </div>

      {/* Відображення списку промокодів */}
      {promocodes.map((promo) => (
        <div
          style={{
            height: 'fit-content',
            width: 'fit-content',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            backgroundColor: '#323232',
            borderRadius: 16,
            padding: 16,
            border: '1px solid #555',
            marginBottom: 10,
          }}
          key={promo.id}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 5,
            }}
          >
            <div>Промокод: {promo.name}</div>
            <div>Відсоток: {promo.percent}%</div>
            <div>Тип: {promo.type === 'sub' ? 'Підписка' : 'Продукт'}</div>
            <div>
              Термін дії:{' '}
              {dayjs(promo.expirationDate.toDate()).format('YYYY-MM-DD')}
            </div>
            <div>Відправлено: {promo.mailed ? 'Так' : 'Ні'}</div>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 16,
            }}
          >
            <button
              style={{
                backgroundColor: '#ff8e04',
                borderRadius: 16,
                color: 'white',
                border: 'none',
                height: 36,
                cursor: 'pointer',
              }}
              onClick={() => handleDelete(promo.id)}
            >
              Видалити
            </button>
            {!promo.mailed && (
              <button
                style={{
                  backgroundColor: '#ff8e04',
                  color: 'white',
                  borderRadius: 16,
                  height: 36,
                  border: 'none',
                  cursor: 'pointer',
                }}
                onClick={() => openModal(promo)}
              >
                Надіслати
              </button>
            )}
          </div>
        </div>
      ))}

      {/* Модальне вікно */}
      {isModalOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: '#1E1E1E',
              padding: '24px',
              borderRadius: '12px',
              width: '480px',
              maxWidth: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
              border: '1px solid #444',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px',
              }}
            >
              <h2
                style={{
                  margin: 0,
                  color: '#FF8E04',
                  fontSize: '20px',
                }}
              >
                Надіслати промокод: {selectedPromo?.name}
              </h2>
              <button
                onClick={closeModal}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#999',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleModalSubmit(onSubmitModal)}>
              {/* Заголовок повідомлення */}
              <div style={modalFormStyles.inputGroup}>
                <label style={modalFormStyles.label}>
                  Заголовок повідомлення:
                </label>
                <Controller
                  control={modalControl}
                  name='modalTitle'
                  rules={{ required: "Заголовок є обов'язковим" }}
                  render={({ field }) => (
                    <>
                      <input
                        {...field}
                        style={modalFormStyles.input}
                        placeholder='Наприклад: Новий промокод для вас!'
                      />
                      {modalErrors.modalTitle && (
                        <div style={modalFormStyles.errorText}>
                          {modalErrors.modalTitle.message}
                        </div>
                      )}
                    </>
                  )}
                />
              </div>

              {/* Текст повідомлення */}
              <div style={modalFormStyles.inputGroup}>
                <label style={modalFormStyles.label}>Текст повідомлення:</label>
                <Controller
                  control={modalControl}
                  name='modalBody'
                  rules={{ required: "Текст є обов'язковим" }}
                  render={({ field }) => (
                    <>
                      <textarea
                        {...field}
                        style={modalFormStyles.textarea}
                        placeholder={`Наприклад: Використайте промокод ${selectedPromo?.name} для отримання знижки ${selectedPromo?.percent}%!`}
                      />
                      {modalErrors.modalBody && (
                        <div style={modalFormStyles.errorText}>
                          {modalErrors.modalBody.message}
                        </div>
                      )}
                    </>
                  )}
                />
              </div>

              {/* Поля для даних повідомлення */}
              <div
                style={{
                  display: 'flex',
                  gap: '16px',
                  marginBottom: '16px',
                }}
              >
                <div style={{ flex: 1 }}>
                  <label style={modalFormStyles.label}>Заголовок даних:</label>
                  <Controller
                    control={modalControl}
                    name='modalDataTitle'
                    render={({ field }) => (
                      <input
                        {...field}
                        style={modalFormStyles.input}
                        placeholder="Необов'язково"
                      />
                    )}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={modalFormStyles.label}>Текст даних:</label>
                  <Controller
                    control={modalControl}
                    name='modalDataText'
                    render={({ field }) => (
                      <input
                        {...field}
                        style={modalFormStyles.input}
                        placeholder="Необов'язково"
                      />
                    )}
                  />
                </div>
              </div>

              {/* Дата та час відправки */}
              <div style={modalFormStyles.inputGroup}>
                <label style={modalFormStyles.label}>
                  Дата та час відправки:
                </label>
                <Controller
                  control={modalControl}
                  name='modalScheduleTime'
                  rules={{
                    required: "Дата та час є обов'язковими",
                    validate: (value) => {
                      const now = new Date();
                      const scheduledDate = new Date(value);
                      return (
                        scheduledDate > now || 'Дата повинна бути у майбутньому'
                      );
                    },
                  }}
                  render={({ field }) => (
                    <>
                      <input
                        type='datetime-local'
                        {...field}
                        style={modalFormStyles.input}
                      />
                      {modalErrors.modalScheduleTime && (
                        <div style={modalFormStyles.errorText}>
                          {modalErrors.modalScheduleTime.message}
                        </div>
                      )}
                    </>
                  )}
                />
              </div>

              {/* Селектор отримувачів */}
              <div style={modalFormStyles.inputGroup}>
                <label style={modalFormStyles.label}>Отримувачі:</label>
                <select
                  value={recipientType}
                  onChange={(e) => setRecipientType(e.target.value)}
                  style={modalFormStyles.select}
                >
                  <option value='all'>
                    Усі {selectedPromo?.type === 'sub' ? 'майстри' : 'клієнти'}
                  </option>
                  <option value='selective'>Вибіркові користувачі</option>
                </select>
              </div>

              {/* Поле для номерів телефонів (якщо вибрано "Вибірково") */}
              {recipientType === 'selective' && (
                <div style={modalFormStyles.inputGroup}>
                  <label style={modalFormStyles.label}>
                    Номери телефонів ("+380..." через кому):
                  </label>
                  <Controller
                    control={modalControl}
                    name='phoneNumbers'
                    rules={{ required: "Номери телефонів є обов'язковими" }}
                    render={({ field }) => (
                      <>
                        <textarea
                          {...field}
                          style={modalFormStyles.textarea}
                          placeholder='+380501234567, +380671234567'
                        />
                        {modalErrors.phoneNumbers && (
                          <div style={modalFormStyles.errorText}>
                            {modalErrors.phoneNumbers.message}
                          </div>
                        )}
                      </>
                    )}
                  />
                </div>
              )}

              {/* Кнопки */}
              <div style={modalFormStyles.buttonGroup}>
                <button
                  type='button'
                  onClick={closeModal}
                  style={{
                    ...modalFormStyles.button,
                    ...modalFormStyles.secondaryButton,
                  }}
                >
                  Скасувати
                </button>
                <button
                  type='submit'
                  style={{
                    ...modalFormStyles.button,
                    ...modalFormStyles.primaryButton,
                  }}
                >
                  Запланувати відправку
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Promo;
